const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/Snackbar/Snackbar.tsx';
import { Alert, Snackbar as MuiSnackbar } from '@mui/material';
import React, { type JSX } from 'react';
import { ColorSettings, getColorSettings, getIcon, Severity } from '../utils';
import { css } from '@emotion/css';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import { removeSnackbar, typeSnackbarActionPayload } from 'App/actions/snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { snackBarList } from 'App/store/ui';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';

interface SnackbarProps {
    text: string;
    id: string;
    color?: ColorSettings | Severity;
    autoHideDuration?: number;
    hideIcon?: boolean;
}

const getSnackbarWrapperCss = (isMediumDevice: boolean) => css`
    bottom: ${isMediumDevice ? '0' : '48px'};
    background-color: transparent;
    position: fixed;
    width: 100%;
    z-index: 100;
    margin-bottom: ${isMediumDevice ? '0' : '8px'};
    pointer-events: none;
`;

const getCssClasses = (color: string, backGroundColor: string, isMediumDevice: boolean) => ({
    snackbarCss: css`
        width: ${isMediumDevice ? '100%' : '560px'};
        left: unset;
        right: unset;
        bottom: unset;
        padding: 5px 10px;
        position: relative;
        margin: auto;
        pointer-events: auto;

        .SnackbarAlert {
            background-color: ${backGroundColor};
            color: ${color};
            width: 100%;
        }
    `,
});

export const Snackbar = (props: SnackbarProps) => {
    const [open, setOpen] = React.useState(true);

    const theme = useTheme();
    const isMediumDevice = useBreakpoint(theme?.breakpoints?.values?.md);

    const { color, backgroundColor, icon } = getColorSettings('snackbar', props.color);
    const classes = getCssClasses(color!, backgroundColor!, isMediumDevice);
    const dispatch = useDispatch();
    const handleClose = () => {
        dispatch(removeSnackbar(props.id));
        setOpen(false);
    };

    const iconElement = !props.hideIcon ? getIcon(icon) : false;
    const autoHideDurationInMilliSeconds = (props.autoHideDuration || 10) * 1000; // MUI Snackbar autohide duration is in milliseconds
    return (
        <MuiSnackbar
            open={open}
            autoHideDuration={autoHideDurationInMilliSeconds}
            className={classes.snackbarCss}
            onClose={handleClose}
        >
            <Alert icon={iconElement} onClose={handleClose} className={'SnackbarAlert'} variant="filled">
                {props.text}
            </Alert>
        </MuiSnackbar>
    );
};

export const SnackbarWrapper = (_props: any): JSX.Element => {
    const snackBarItems: typeSnackbarActionPayload[] = useSelector(snackBarList);
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMediumDevice = useBreakpoint(theme?.breakpoints?.values?.md);
    const snackBarWrapperCss = getSnackbarWrapperCss(isMediumDevice);
    return (
        <div className={snackBarWrapperCss}>
            {snackBarItems.map(entry => (
                <Snackbar
                    key={entry.id}
                    color={entry.color}
                    text={formatMessage(entry.message)}
                    id={entry.id}
                    autoHideDuration={entry.autoHideDuration}
                />
            ))}
        </div>
    );
};
